import React from 'react';
import classNames from 'classnames';
import { IMAGE_TYPE } from './types.js';
import { uniqueId, includes, get, isFunction } from 'lodash';
import { addAnchorTagToUrl, anchorScroll, isSSR } from 'wix-rich-content-common';
import { initCustomElement, Image as Image$1 } from '@wix/image';
import { DEFAULTS } from './consts.js';
import expand from './icons/expand.js';
import InPluginInput from './InPluginInput.js';
import 'ricos-schema';
import './style-inject.es-746bb8ed.js';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
initCustomElement();
const IMAGE_FALLBACK_WIDTH = 300;
const getImageSrc = (src, customGetImageUrl) => {
  if (typeof src === "object") {
    if (src.source) {
      if (src.source === "static") {
        if (src.url) {
          return src.url;
        } else {
          console.error("must provide src url when using static image source!", src);
        }
      } else if (src.source === "custom") {
        if (customGetImageUrl) {
          return customGetImageUrl({ file_name: src.file_name });
        } else {
          console.error("must provide getImageUrl helper when using custom image source!", src);
        }
      }
    } else if (src.file_name) {
      return src.file_name;
    }
  }
  return src;
};
class WixImage extends React.Component {
  constructor(props) {
    super(props);
    this.handleExpand = (e) => {
      var _a;
      e.preventDefault();
      const {
        settings: { onExpand },
        helpers = {}
      } = this.props;
      (_a = helpers.onViewerAction) == null ? void 0 : _a.call(helpers, IMAGE_TYPE, "Click", "expand_image");
      this.hasExpand() && (onExpand == null ? void 0 : onExpand(this.props.blockKey));
    };
    this.scrollToAnchor = (e) => {
      const {
        componentData: {
          config: { link: { anchor } = {} }
        },
        customAnchorScroll
      } = this.props;
      if (customAnchorScroll) {
        customAnchorScroll(e, anchor);
      } else {
        const anchorString = `viewer-${anchor}`;
        const element = document.getElementById(anchorString);
        addAnchorTagToUrl(anchorString);
        anchorScroll(element);
      }
    };
    this.hasLink = () => {
      var _a, _b, _c;
      return (_c = (_b = (_a = this.props.componentData) == null ? void 0 : _a.config) == null ? void 0 : _b.link) == null ? void 0 : _c.url;
    };
    this.hasAnchor = () => {
      var _a, _b, _c;
      return (_c = (_b = (_a = this.props.componentData) == null ? void 0 : _a.config) == null ? void 0 : _b.link) == null ? void 0 : _c.anchor;
    };
    this.onKeyDown = (e) => {
      if ((e.key === "Enter" || e.key === " ") && !this.props.getInPluginEditingMode) {
        this.handleClick(e);
      }
    };
    this.handleClick = (e) => {
      if (this.hasLink()) {
        return null;
      } else if (this.hasAnchor()) {
        e.preventDefault();
        e.stopPropagation();
        this.scrollToAnchor(e);
      } else {
        this.handleExpand(e);
      }
    };
    this.handleContextMenu = (e) => {
      const {
        componentData: { disableDownload = false }
      } = this.props;
      return disableDownload && e.preventDefault();
    };
    this.hasExpand = () => {
      const { componentData, settings } = this.props;
      let disableExpand = false;
      if (componentData.disableExpand !== void 0) {
        disableExpand = componentData.disableExpand;
      } else if (settings.disableExpand !== void 0) {
        disableExpand = settings.disableExpand;
      }
      return !disableExpand && settings.onExpand;
    };
    this.renderExpandIcon = () => {
      const {
        styles: { expandContainer, expandIcon }
      } = this.props;
      return /* @__PURE__ */ React.createElement("div", { className: expandContainer }, /* @__PURE__ */ React.createElement(expand, { className: expandIcon, onClick: this.handleExpand }));
    };
    this.getImgSize = (imgSrc) => {
      const newImg = new Image();
      newImg.onload = () => this.setState({ dimensions: { width: newImg.width, height: newImg.height } });
      newImg.src = imgSrc;
    };
    this._id = uniqueId("new-image");
  }
  getImageDataUrl() {
    return this.props.dataUrl ? this.props.dataUrl : null;
  }
  renderTitle(data) {
    const config = data.config || {};
    return !!config.showTitle && /* @__PURE__ */ React.createElement("div", { className: classNames(this.props.styles.imageTitle) }, data && data.title || "");
  }
  renderDescription(data) {
    const config = data.config || {};
    return !!config.showDescription && /* @__PURE__ */ React.createElement("div", { className: classNames(this.props.styles.imageDescription) }, data && data.description || "");
  }
  renderCaption(caption) {
    const {
      onCaptionChange,
      setFocusToBlock,
      setInPluginEditingMode,
      styles: { imageCaption, link }
    } = this.props;
    const classes = classNames(imageCaption, this.hasLink() && link);
    return onCaptionChange ? /* @__PURE__ */ React.createElement(
      InPluginInput,
      {
        setInPluginEditingMode,
        className: classes,
        value: caption,
        onChange: onCaptionChange,
        setFocusToBlock
      }
    ) : /* @__PURE__ */ React.createElement("span", { dir: "auto", className: classes }, caption);
  }
  shouldRenderCaption() {
    var _a;
    const { getInPluginEditingMode, settings, componentData, defaultCaption } = this.props;
    const caption = (_a = componentData.metadata) == null ? void 0 : _a.caption;
    if (includes(get(settings, "toolbar.hidden"), "settings")) {
      return false;
    }
    if (caption === void 0 || caption === "" && !(getInPluginEditingMode == null ? void 0 : getInPluginEditingMode()) || caption === defaultCaption) {
      return false;
    }
    const data = componentData || DEFAULTS;
    if (data.config.size === "original" && data.src && data.src.width) {
      return data.src.width >= 350;
    }
    return true;
  }
  // eslint-disable-next-line complexity
  render() {
    var _a, _b, _c, _d, _e;
    const {
      componentData,
      className,
      settings,
      setComponentUrl,
      helpers,
      getInPluginEditingMode,
      styles,
      isMobile,
      seoMode,
      accessibilityProps
    } = this.props;
    const data = componentData || DEFAULTS;
    let { metadata, config: { alignment, width, size } = {} } = componentData;
    if (!metadata) {
      metadata = {};
    }
    const itemClassName = classNames(styles.imageWrapper, styles.imageRatio, className, {
      [styles.pointer]: this.hasExpand()
    });
    const imageSrc = this.getImageDataUrl() || getImageSrc(data.src, helpers == null ? void 0 : helpers.getImageUrl);
    let imageProps = {};
    if (data.src && settings && settings.imageProps) {
      imageProps = isFunction(settings.imageProps) ? settings.imageProps(data.src) : settings.imageProps;
    }
    const socialAttrs = {};
    ["data-pin-nopin", "data-pin-url", "data-pin-media"].forEach(
      (attr) => imageProps[attr] && (socialAttrs[attr] = imageProps[attr])
    );
    setComponentUrl == null ? void 0 : setComponentUrl(imageSrc);
    const isEditor = getInPluginEditingMode;
    const usePredefinedWidth = (alignment === "left" || alignment === "right") && !width && size !== "small" && size !== "original" && !isMobile;
    const dim = {
      width: ((_a = data.src) == null ? void 0 : _a.width) || data.width || ((_b = this.state) == null ? void 0 : _b.dimensions.width),
      height: ((_c = data.src) == null ? void 0 : _c.height) || data.height || ((_d = this.state) == null ? void 0 : _d.dimensions.height)
    };
    const isDimensionsExists = dim.width && dim.height;
    if (!isDimensionsExists && !isSSR()) {
      this.getImgSize(imageSrc);
    }
    const customWidthStyle = size === "original" || isMobile && size === "inline" && data.config.width && data.config.width > 150 ? { width: ((_e = data.src) == null ? void 0 : _e.width) + "px", maxWidth: "100%" } : {};
    return /* @__PURE__ */ React.createElement(
      "div",
      __spreadValues({
        "data-hook": "imageViewer",
        className: styles.imageContainer,
        onContextMenu: this.handleContextMenu,
        onKeyDown: this.onKeyDown
      }, accessibilityProps),
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: itemClassName,
          id: this._id,
          "aria-label": metadata.alt,
          onClick: this.handleClick,
          onKeyDown: this.onKeyDown,
          style: __spreadValues(__spreadValues({
            "--dim-height": dim.height,
            "--dim-width": dim.width
          }, usePredefinedWidth && { width: IMAGE_FALLBACK_WIDTH + "px" }), customWidthStyle)
        },
        isDimensionsExists && /* @__PURE__ */ React.createElement(
          Image$1,
          __spreadValues({
            id: imageSrc,
            containerId: this._id,
            displayMode: "fill",
            width: dim.width,
            height: dim.height,
            uri: imageSrc,
            alt: metadata.alt || "",
            socialAttrs,
            isSEOBot: !!seoMode,
            imageStyles: {
              maxWidth: "100%"
            }
          }, !isEditor && {
            placeholderTransition: seoMode ? void 0 : "blur",
            shouldUseLQIP: !settings.disableTransition
          })
        ),
        this.hasExpand() && this.renderExpandIcon()
      ),
      this.renderTitle(data),
      this.renderDescription(data),
      this.shouldRenderCaption() && this.renderCaption(metadata.caption)
    );
  }
}

export { WixImage as default };
//# sourceMappingURL=wixImage.js.map
